import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Shield, FileText, Cookie } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { API_BASE as API } from '../lib/apiBase';


export default function LegalPage() {
  const { lang } = useLang();
  const [searchParams] = useSearchParams();
  const page = searchParams.get('page') || 'privacy';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const endpoint = page === 'terms' ? 'terms' : page === 'cookies' ? 'cookie-policy' : 'privacy';
    fetch(`${API}/api/legal/${endpoint}?lang=${lang}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page, lang]);

  const tabs = [
    { id: 'privacy', label: lang === 'es' ? 'Privacidad' : 'Privacy', icon: Shield },
    { id: 'terms', label: lang === 'es' ? 'Terminos' : 'Terms', icon: FileText },
    { id: 'cookies', label: 'Cookies', icon: Cookie },
  ];

  return (
    <div data-testid="legal-page" className="min-h-screen pt-14 pb-32">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-pulse-muted text-sm mb-6 hover:text-white transition-colors">
          <ArrowLeft size={16} /> {lang === 'es' ? 'Volver' : 'Back'}
        </Link>

        <div className="flex gap-1 mb-8 bg-pulse-surface rounded-xl p-1 w-fit">
          {tabs.map(t => (
            <Link key={t.id} to={`/legal?page=${t.id}`} data-testid={`legal-tab-${t.id}`} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${page === t.id ? 'bg-pulse-primary text-black' : 'text-pulse-muted hover:text-white'}`}>
              <t.icon size={14} /> {t.label}
            </Link>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : data ? (
          <div className="bg-pulse-surface border border-pulse-border rounded-2xl p-6 md:p-8">
            <h1 className="font-heading font-bold text-2xl md:text-3xl mb-2">{data.title}</h1>
            {data.last_updated && <p className="text-xs text-pulse-muted mb-8">{lang === 'es' ? 'Ultima actualizacion' : 'Last updated'}: {data.last_updated}</p>}

            {data.sections ? (
              <div className="space-y-6">
                {data.sections.map((s, i) => (
                  <div key={i}>
                    <h2 className="font-heading font-semibold text-base mb-2">{s.heading}</h2>
                    <p className="text-sm text-pulse-muted leading-relaxed">{s.content}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <p className="text-sm text-pulse-muted leading-relaxed mb-4">{data.content}</p>
                {data.types && (
                  <div className="space-y-2 mt-4">
                    {data.types.map((c, i) => (
                      <div key={i} className="flex items-center gap-4 p-3 bg-pulse-elevated rounded-xl text-sm">
                        <span className="font-mono text-xs text-pulse-primary">{c.name}</span>
                        <span className="flex-1 text-pulse-muted">{c.purpose}</span>
                        <span className="text-xs text-pulse-muted">{c.duration}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.essential ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{c.essential ? (lang === 'es' ? 'Esencial' : 'Essential') : 'Opcional'}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-pulse-muted text-center py-12">{lang === 'es' ? 'Error cargando contenido' : 'Error loading content'}</p>
        )}
      </div>
    </div>
  );
}
